import { useMemo, useRef, useState } from 'react';

import useGetProjectPublishedStatus from '@/connection/projects/use-get-project-published-status';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject, getIsLoadingWorkingProject, getPublishedStatus } from '@/store/slices/project/selectors';
import { getUser } from '@/store/slices/user/selectors';
import { EProjectStatus } from '@/types/projects.types';
import copyEventUrl from '@/utils/copy-event-url';
import { getChannels } from '@/store/slices/channels/selectors';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import { TooltipPortalContentDirection, TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import Text from '@ui/text/text/text';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';

type TCopyLinkButtonProps = {
  sessionUuid?: string;
  language?: string;
  size?: number;
  iconName?: string;
};

const CopyLinkButton = ({ sessionUuid, language, size = 16, iconName = ICONS.CHAIN_LINK }: TCopyLinkButtonProps) => {
  const copyTimeout = useRef<NodeJS.Timeout | null>(null);

  const workingProject = useAppSelector(getWorkingProject);
  const loadingWorkingProject = useAppSelector(getIsLoadingWorkingProject);
  const publishedStatus = useAppSelector(getPublishedStatus);
  const channels = useAppSelector(getChannels);
  const user = useAppSelector(getUser);

  useGetProjectPublishedStatus();

  const [copied, setCopied] = useState(false);

  const publishedUrl = publishedStatus?.url;

  const channel = useMemo(() => {
    if (user && channels) {
      return channels.find((channel) => channel.channel === user.active_channel);
    }
    return null;
  }, [channels, user]);

  const handleCopyClick = () => {
    if (copyTimeout.current) {
      clearTimeout(copyTimeout.current);
    }
    const _copied = copyEventUrl({ channel, event: workingProject, publishedUrl, sessionUuid, language });

    setCopied(_copied);

    copyTimeout.current = setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const isDisabled =
    !channel ||
    !publishedUrl ||
    loadingWorkingProject ||
    !workingProject ||
    publishedStatus?.status !== EProjectStatus.PUBLIC;

  return (
    <TooltipPortal
      position={TooltipPortalPosition.BOTTOM}
      contentDirection={TooltipPortalContentDirection.CENTER}
      verticalOffset={8}
      disabled={!!publishedUrl}
      showOnFocus
      styles={{ padding: '8px', backgroundColor: 'var(--gray-true-dark-700)' }}
      tooltipWrapperClass="position-relative"
      tooltipContent={
        <Text textAlign="center" className="P2" color="var(--gray-true-dark-100)">
          Project not yet <br /> published
        </Text>
      }
      keepOpenOnContentHover={false}
    >
      <Container
        background="var(--gray-900)"
        padding="4px 8px"
        className="transition-all pointer-events-none radius-8 L2 position-absolute top--24 left-50-percent"
        style={{
          transform: copied ? 'translate(-50%, -4px)' : 'translate(-50%, 50%)',
          opacity: copied ? 1 : 0,
        }}
      >
        Copied
      </Container>
      <Button
        className="flex fd-row jc-center ai-center radius-full padding-0"
        width={`${size * 2}px`}
        height={`${size * 2}px`}
        disabled={isDisabled}
        onClick={handleCopyClick}
        design={TButtonAndLinkDesigns.LocalBttnSec}
      >
        <Icon name={iconName} size={size} color="var(--gray-200)" />
      </Button>
    </TooltipPortal>
  );
};

export default CopyLinkButton;
